import type { suche } from "@progressive-development/politik-model";

import type { SitzungenFilter } from "./sitzungen-abfrage.js";

/**
 * Was die Sitzungen-Seite im Browser rechnet — reine Funktionen
 * (FACHKONZEPT-termine.md, 3 und 5). Filter, Facetten mit Zahlen, die
 * Tagesgruppen der Liste und die Form, die pd-calendar liest.
 *
 * ZEIT WIE GELIEFERT: `beginn` trägt die Kölner Zeitzone. Tag und Uhrzeit
 * werden aus dem String gelesen, nicht über `Date` in die Zeitzone des
 * Betrachters gedreht — eine Sitzung um 17 Uhr in Köln ist auch für
 * jemanden in Lissabon eine Sitzung um 17 Uhr in Köln.
 */

/** `YYYY-MM-DD` aus dem ISO-Zeitpunkt — der Schlüssel des Kalenders. */
export function tagVon(e: suche.SitzungenEintrag): string {
  return e.beginn.slice(0, 10);
}

/** `HH:MM` aus dem ISO-Zeitpunkt; nichts, wenn die Uhrzeit offen ist. */
export function uhrzeitVon(iso: string | undefined, offen?: boolean): string | undefined {
  if (!iso || offen) return undefined;
  const t = iso.slice(11, 16);
  return /^\d{2}:\d{2}$/.test(t) ? t : undefined;
}

/** `YYYY-MM` — der Monatsschlüssel des URL. */
export function monatVon(tag: string): string {
  return tag.slice(0, 7);
}

// =============================================================================
// Berichtssitzungen — ausgeblendet, aber genannt
// =============================================================================

/**
 * Die Liste beginnt heute (8.3) und wächst auf Wunsch nach hinten: `zurueck`
 * Schritte zu je drei Monaten. Der Anfang wird über den Monat gerechnet, nicht
 * über Tage: drei Monate zurück ist der 1. des Monats, den Menschen meinen.
 */
export function listenAnfang(heute: string, zurueck: number): string {
  if (zurueck <= 0) return heute;
  const [j, m] = heute.split("-").map(Number);
  const gesamt = j * 12 + (m - 1) - zurueck * 3;
  const jahr = Math.floor(gesamt / 12);
  const monat = (gesamt % 12) + 1;
  return `${jahr}-${String(monat).padStart(2, "0")}-01`;
}

/**
 * Trennt die Sitzungen von den „Berichtssitzungen" des Digitalen
 * Berichtswesens (`art: "berichtswesen"`, kuratiert in der Pipeline). Sie
 * sind Sammelvorgänge mit Platzhalter-Datum, keine Termine — im Kalender
 * wären sie sechs ganztägige Einträge am Silvestertag. Die Zahl wird
 * gezeigt, nicht verschwiegen.
 */
export function ohneBerichtswesen(eintraege: readonly suche.SitzungenEintrag[]): {
  sitzungen: suche.SitzungenEintrag[];
  ausgeblendet: number;
} {
  const sitzungen = eintraege.filter((e) => e.art !== "berichtswesen");
  return { sitzungen, ausgeblendet: eintraege.length - sitzungen.length };
}

// =============================================================================
// Filter und Facetten
// =============================================================================

export type Gruppe = "ebene" | "gremium" | "bezirk";
export const GRUPPEN: readonly Gruppe[] = ["ebene", "gremium", "bezirk"];

export function passtGruppe(e: suche.SitzungenEintrag, f: SitzungenFilter, g: Gruppe): boolean {
  switch (g) {
    case "ebene":
      return f.ebene.length === 0 || f.ebene.includes(e.ebene);
    case "gremium":
      return f.gremium.length === 0 || (!!e.gremium && f.gremium.includes(e.gremium));
    case "bezirk":
      return f.bezirk.length === 0 || (!!e.bezirk && f.bezirk.includes(e.bezirk));
    default:
      return true;
  }
}

export function passtFilter(e: suche.SitzungenEintrag, f: SitzungenFilter): boolean {
  return GRUPPEN.every((g) => passtGruppe(e, f, g));
}

export function filtere(
  eintraege: readonly suche.SitzungenEintrag[],
  f: SitzungenFilter,
): suche.SitzungenEintrag[] {
  return eintraege.filter((e) => passtFilter(e, f));
}

export interface Facetten {
  ebene: Map<string, number>;
  /** Gremium-Slug → Zahl; nur Einträge mit Slug. */
  gremium: Map<string, number>;
  /** Slug → Name, aus den Einträgen selbst — kein Verzeichnis nötig. */
  gremiumNamen: Map<string, string>;
  bezirk: Map<string, number>;
  gesamt: number;
}

const zaehle = (map: Map<string, number>, k: string | undefined): void => {
  if (!k) return;
  map.set(k, (map.get(k) ?? 0) + 1);
};

/**
 * Standard-Facettierung wie bei den Drucksachen: Jede Gruppe zählt, was die
 * ANDEREN Gruppen übrig lassen — die Werte der eigenen Gruppe sind
 * Alternativen. Ein Durchlauf.
 */
export function facetten(
  eintraege: readonly suche.SitzungenEintrag[],
  f: SitzungenFilter,
): Facetten {
  const fac: Facetten = {
    ebene: new Map(),
    gremium: new Map(),
    gremiumNamen: new Map(),
    bezirk: new Map(),
    gesamt: 0,
  };
  for (const e of eintraege) {
    if (e.gremium) fac.gremiumNamen.set(e.gremium, e.gremiumName);
    let verfehlt: Gruppe | undefined;
    let mehrfach = false;
    for (const g of GRUPPEN) {
      if (!passtGruppe(e, f, g)) {
        if (verfehlt !== undefined) {
          mehrfach = true;
          break;
        }
        verfehlt = g;
      }
    }
    if (mehrfach) continue;
    const zaehltIn = (g: Gruppe) => verfehlt === undefined || verfehlt === g;
    if (verfehlt === undefined) fac.gesamt += 1;
    if (zaehltIn("ebene")) zaehle(fac.ebene, e.ebene);
    if (zaehltIn("gremium")) zaehle(fac.gremium, e.gremium);
    if (zaehltIn("bezirk")) zaehle(fac.bezirk, e.bezirk);
  }
  return fac;
}

// =============================================================================
// Die Liste: Tagesgruppen ab heute
// =============================================================================

export interface Tagesgruppe {
  tag: string;
  eintraege: suche.SitzungenEintrag[];
}

/**
 * Die Liste beginnt heute (8.3): Einträge ab `heute` (einschließlich),
 * chronologisch, nach Tag gruppiert. Innerhalb eines Tages in der Reihenfolge
 * der Quelle — sie ist nach Beginn sortiert, und Gleichzeitiges bleibt, wie
 * es kommt.
 */
export function tagesgruppen(
  eintraege: readonly suche.SitzungenEintrag[],
  heute: string,
): Tagesgruppe[] {
  const gruppen = new Map<string, suche.SitzungenEintrag[]>();
  for (const e of [...eintraege].sort((a, b) => a.beginn.localeCompare(b.beginn))) {
    const tag = tagVon(e);
    if (tag < heute) continue;
    const g = gruppen.get(tag) ?? [];
    g.push(e);
    gruppen.set(tag, g);
  }
  return [...gruppen.entries()].map(([tag, es]) => ({ tag, eintraege: es }));
}

// =============================================================================
// Stand der Unterlagen
// =============================================================================

export type UnterlagenStand =
  "abgesagt" | "tagesordnung" | "tagesordnung-folgt" | "niederschrift" | "niederschrift-folgt";

/**
 * Was zu einer Sitzung vorliegt — ein Wort je Zustand, das die Zeile
 * beschriftet. Geplant: Tagesordnung liegt vor / folgt. Durchgeführt:
 * Niederschrift liegt vor / folgt. Nichts wird aus dem Datum geschlossen —
 * `status` sagt, was gilt.
 *
 * ABGESAGT WIRD ZUERST GEPRÜFT, und zwar hier drin, nicht an den Aufrufstellen.
 * Eine abgesagte Sitzung mit Tagesordnung ist trotzdem abgesagt; ohne diesen
 * Zweig fiele sie in den Tagesordnungs-Zweig und die Zeile behauptete, es gehe
 * weiter. Die Regel an jede Aufrufstelle zu schreiben hieße, sich darauf zu
 * verlassen, dass niemand sie vergisst — die Funktion beantwortet die Frage
 * lieber vollständig.
 *
 * Der Parameter ist STRUKTURELL, nicht `suche.SitzungenEintrag`: Dieselbe
 * Frage stellt auch die Startseite, deren Aufmacher-Einträge
 * (`startseite.TerminEintrag`) dieselben drei Felder tragen, aber nicht die
 * übrigen. Die Funktion liest ohnehin nur diese drei.
 */
export function unterlagenStand(e: {
  status: string;
  tagesordnung: number;
  dokumente?: { niederschrift?: boolean };
}): UnterlagenStand {
  if (e.status === "abgesagt") return "abgesagt";
  if (e.status === "durchgefuehrt") {
    return e.dokumente?.niederschrift ? "niederschrift" : "niederschrift-folgt";
  }
  return e.tagesordnung > 0 ? "tagesordnung" : "tagesordnung-folgt";
}

// =============================================================================
// Die Form, die pd-calendar liest
// =============================================================================

/** Ein Kalender-Eintrag — strukturgleich mit `CalendarCellInfo` von pd-calendar. */
export interface KalenderEintrag {
  id: string;
  info: string;
  desc?: string;
  startTime?: string;
  endTime?: string;
  fullDay?: boolean;
  location?: string;
  category?: string;
}

/**
 * Sitzungen → `Record<Tag, KalenderEintrag[]>`. Titel ist das Gremium (das
 * ist, was im Raster lesbar sein muss), Beschreibung der Sitzungsname,
 * Kategorie die Ebene. Ohne Uhrzeit ganztägig — der Kalender stellt es oben
 * an, die Zeile sagt „Uhrzeit folgt".
 */
export function kalenderDaten(
  eintraege: readonly suche.SitzungenEintrag[],
): Record<string, KalenderEintrag[]> {
  const daten: Record<string, KalenderEintrag[]> = {};
  for (const e of eintraege) {
    const tag = tagVon(e);
    const start = uhrzeitVon(e.beginn, e.uhrzeitOffen);
    const eintrag: KalenderEintrag = {
      id: e.id,
      info: e.gremiumName,
      desc: e.name,
      startTime: start,
      endTime: start ? uhrzeitVon(e.ende) : undefined,
      fullDay: !start,
      location: e.ort,
      category: e.ebene,
    };
    (daten[tag] ??= []).push(eintrag);
  }
  return daten;
}
